import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import TruckLoader from '../components/TruckLoader';
import AppIcon from '../components/AppIcon';
import { auditLog } from '../utils/auditLog';
import { friendlyError } from '../utils/friendlyError';

import './SolShell.css';

const CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const SIGNED_URL_TIMEOUT_MS = 25000;
const INGEST_TIMEOUT_MS = 60000;

const statusColor = {
  idle: '#94a3b8',
  uploading: '#3b82f6',
  bucket_loaded: '#0ea5e9',
  db_loading: '#f59e0b',
  kpi_refreshing: '#8b5cf6',
  skipped: '#0891b2',
  success: '#22c55e',
  error: '#ef4444',
};

/* ── helpers ──────────────────────────────────────────────────────────── */

function fmtBytes(bytes) {
  if (!bytes) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleString();
}

function isNetworkLikeError(err) {
  const msg = (err?.message || String(err || '')).toLowerCase();
  return (
    msg.includes('network error') ||
    msg.includes('failed to fetch') ||
    msg.includes('unable to reach the server') ||
    msg.includes('timed out') ||
    msg.includes('timeout') ||
    msg.includes('gcs upload failed')
  );
}

async function fetchWithTimeout(fetchFn, url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchFn(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/* ── Upload row state ─────────────────────────────────────────────────── */

function UploadRow({ item, onRemove }) {
  const { file, fileType, status, progress, gcsPath, error, loadDetails } = item;

  const pct = Math.round(progress || 0);
  const color = statusColor[status] || '#94a3b8';

  const stepState = {
    bucket: status === 'uploading' ? 'active' : (['bucket_loaded', 'db_loading', 'kpi_refreshing', 'success', 'skipped'].includes(status) ? 'done' : 'pending'),
    db: status === 'db_loading' ? 'active' : (['kpi_refreshing', 'success', 'skipped'].includes(status) ? 'done' : 'pending'),
    kpi: status === 'kpi_refreshing' ? 'active' : (['success', 'skipped'].includes(status) ? 'done' : 'pending'),
    refresh: ['success', 'skipped'].includes(status) ? 'done' : 'pending',
  };

  const stepLabel = (state) => {
    if (state === 'done') return '✓';
    if (state === 'active') return '…';
    return '○';
  };

  const friendlyStatus = {
    idle: 'idle',
    uploading: 'uploading to bucket',
    bucket_loaded: 'loaded to gcp bucket',
    db_loading: 'now loading to db',
    kpi_refreshing: 'data loading to kpi tables',
    skipped: 'already loaded',
    success: 'data is refreshed',
    error: 'error',
  };

  return (
    <div style={{
      background: '#f8fafc', borderRadius: 8, padding: '12px 16px', marginBottom: 10,
      border: `1px solid ${color}33`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {file.name}
          </div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
            {fmtBytes(file.size)} · {fileType.toUpperCase()}
            {gcsPath && <span style={{ color: '#94a3b8', marginLeft: 8 }}> → {gcsPath}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 12 }}>
          <span style={{ color, fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
            {status === 'uploading' ? (pct > 0 ? `${pct}%` : 'starting...') : (friendlyStatus[status] || status)}
          </span>
          {(status === 'success' || status === 'skipped' || status === 'error' || status === 'idle') && (
            <button onClick={() => onRemove(item.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1 }}>
              ×
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {(status === 'uploading' || status === 'db_loading' || status === 'kpi_refreshing') && (
        <div style={{ marginTop: 8, height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${status === 'uploading' ? pct : 100}%`, background: '#3b82f6', transition: 'width 0.2s ease', borderRadius: 2 }} />
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', display: 'grid', gridTemplateColumns: '1fr', rowGap: 3 }}>
        <div>{stepLabel(stepState.bucket)} 1. Loaded to GCP bucket</div>
        <div>{stepLabel(stepState.db)} 2. Now loading to DB</div>
        <div>{stepLabel(stepState.kpi)} 3. Data loading to KPI tables</div>
        <div>{stepLabel(stepState.refresh)} 4. Data is refreshed</div>
      </div>

      {(status === 'success' || status === 'skipped') && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#22c55e' }}>
          {status === 'skipped'
            ? '✓ File already loaded. Duplicate upload was skipped.'
            : '✓ Uploaded and loaded to DB.'}
          {loadDetails && status !== 'skipped' && (
            <span>
              {' '}Paylocity rows: {loadDetails.paylocity_rows ?? 0}, WEX rows: {loadDetails.wex_rows ?? 0}
              {loadDetails.rollup_refresh === 'completed'
              ? ' (dashboard refreshed)'
              : loadDetails.rollup_refresh === 'triggered'
                ? ' (dashboard refreshing in background)'
                : ''}
            </span>
          )}
        </div>
      )}
      {status === 'error' && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444' }}>
          <div>✗ {error || 'Upload failed. Please try again.'}</div>
        </div>
      )}
    </div>
  );
}

/* ── Recent uploads table ─────────────────────────────────────────────── */

function RecentUploads({ apiBaseUrl, fetchWithAuthRetry, fileType }) {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuthRetry(
        `${apiBaseUrl}/api/uploads/list?file_type=${fileType}`,
        {}
      );
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [apiBaseUrl, fetchWithAuthRetry, fileType]);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !files) return <TruckLoader message="Loading recent uploads…" size="sm" />;
  if (!files) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div className="chart-card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="chart-title">Recent Uploads</div>
          <button onClick={load} disabled={loading} className="header-btn" style={{ padding: '6px 12px', fontSize: 12 }}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {files.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>No uploads found in recent months.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f, i) => (
                  <tr key={i}>
                    <td>{f.filename}</td>
                    <td style={{ textTransform: 'uppercase' }}>{f.file_type}</td>
                    <td>
                      <div style={{ textTransform: 'capitalize' }}>
                        {f.status === 'skipped_duplicate' ? 'Skipped (duplicate)' :
                         f.status === 'uploaded' ? 'Uploaded (pending ingest)' :
                         f.status === 'processed' ? 'Processed' :
                         f.status === 'loaded' ? 'Loaded' :
                         f.status === 'error' ? 'Error' :
                         (f.status || 'Uploaded')}
                      </div>
                      {f.status_detail && (
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, lineHeight: 1.3 }}>
                          {f.status_detail}
                        </div>
                      )}
                    </td>
                    <td>{fmtBytes(f.size_bytes)}</td>
                    <td>{fmtDate(f.uploaded_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────── */

export default function FileUploadPage() {
  const { getIdToken, fetchWithAuthRetry, user, isAdmin, isUploader, canUpload, signOut } = useAuth();
  const uploaderOnly = isUploader && !isAdmin;
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

  const handleLogout = async () => { await signOut(); navigate('/'); };

  const [fileType, setFileType] = useState('paylocity');
  const [queue, setQueue] = useState([]);       // UploadItem[]
  const [dragging, setDragging] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fileInputRef = useRef();
  const nextId = useRef(1);

  /* user display */
  const displayName = useMemo(() => user?.displayName || user?.email || 'User', [user]);
  const avatar = useMemo(() => {
    const b = (user?.displayName || user?.email || 'U').trim();
    const p = b.split(/\s+/).filter(Boolean);
    return (p.length >= 2 ? `${p[0][0]}${p[1][0]}` : `${b[0]}`).toUpperCase();
  }, [user]);

  /* ── Drop zone ── */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    addFiles(files);
  }, [fileType]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: addFiles regenerates on fileType change just like handleDrop does,
  // so [fileType] keeps handleDrop in sync with the latest addFiles.

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const addFiles = useCallback((files) => {
    const items = files
      .filter(f => f.name.match(/\.(xlsx|xls)$/i))
      .map(f => ({
        id: nextId.current++,
        file: f,
        fileType,
        status: 'idle',
        progress: 0,
        gcsPath: null,
        error: null,
        loadDetails: null,
      }));
    if (items.length === 0) return;
    setQueue(q => [...q, ...items]);
    // Auto-start upload for each
    items.forEach(item => uploadFile(item));
  }, [fileType]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: uploadFile is stable (its own deps are all stable refs) so it is
  // safe to omit from this array. Including it would create a TDZ error
  // since uploadFile is declared further below.

  const updateItem = useCallback((id, patch) => {
    setQueue(q => q.map(item => item.id === id ? { ...item, ...patch } : item));
  }, []);

  const removeItem = useCallback((id) => {
    setQueue(q => q.filter(item => item.id !== id));
  }, []);

  /* ── Core upload ── */
  // Uploads a single file to GCS for a given file_type; returns the gcs_path
  const uploadToGcs = useCallback(async (id, file, ft, progressWeight = { start: 0, end: 100 }) => {
    const res = await fetchWithTimeout(
      fetchWithAuthRetry,
      `${apiBaseUrl}/api/uploads/signed-url`,
      {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_type: ft, filename: file.name }),
      },
      SIGNED_URL_TIMEOUT_MS,
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // FastAPI 422 returns detail as an array of Pydantic validation objects;
      // other errors return detail as a plain string.
      const rawDetail = body.detail;
      let errMsg;
      if (typeof rawDetail === 'string') {
        errMsg = rawDetail;
      } else if (Array.isArray(rawDetail)) {
        errMsg = rawDetail.map(d => `${(d.loc || []).join(' → ')}: ${d.msg}`).join('; ');
      } else {
        errMsg = `Server error ${res.status}`;
      }
      throw new Error(errMsg);
    }
    const { upload_url, gcs_path } = await res.json();
    // Signed URL obtained; keep users informed before first upload progress event arrives.
    updateItem(id, { progress: Math.max(progressWeight.start, 1) });

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', upload_url, true);
      xhr.setRequestHeader('Content-Type', CONTENT_TYPE);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = progressWeight.start + Math.round((e.loaded / e.total) * (progressWeight.end - progressWeight.start));
          updateItem(id, { progress: pct });
        }
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`GCS upload failed: HTTP ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });

    return gcs_path;
  }, [apiBaseUrl, fetchWithAuthRetry, updateItem]);

  const directUploadToBackend = useCallback(async (id, file, ft) => {
    const formData = new FormData();
    formData.append('file_type', ft);
    formData.append('file', file, file.name);

    updateItem(id, { progress: 5 });
    const res = await fetchWithTimeout(
      fetchWithAuthRetry,
      `${apiBaseUrl}/api/uploads/direct-upload`,
      {
        method: 'POST',
        body: formData,
      },
      INGEST_TIMEOUT_MS,
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const rawDetail = body.detail;
      let errMsg;
      if (typeof rawDetail === 'string') {
        errMsg = rawDetail;
      } else if (Array.isArray(rawDetail)) {
        errMsg = rawDetail.map(d => `${(d.loc || []).join(' -> ')}: ${d.msg}`).join('; ');
      } else {
        errMsg = `Direct upload failed: server error ${res.status}`;
      }
      throw new Error(errMsg);
    }
    updateItem(id, { progress: 100 });
    return res.json();
  }, [apiBaseUrl, fetchWithAuthRetry, updateItem]);

  const ingestFromGcs = useCallback(async (ft, gcsPath) => {
    const res = await fetchWithTimeout(
      fetchWithAuthRetry,
      `${apiBaseUrl}/api/uploads/ingest`,
      {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_type: ft, gcs_path: gcsPath }),
      },
      INGEST_TIMEOUT_MS,
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const rawDetail = body.detail;
      let errMsg;
      if (typeof rawDetail === 'string') {
        errMsg = rawDetail;
      } else if (Array.isArray(rawDetail)) {
        errMsg = rawDetail.map(d => `${(d.loc || []).join(' → ')}: ${d.msg}`).join('; ');
      } else {
        errMsg = `Ingest failed: server error ${res.status}`;
      }
      throw new Error(errMsg);
    }
    return res.json();
  }, [apiBaseUrl, fetchWithAuthRetry]);

  const uploadFile = useCallback(async (item) => {
    const { id, file, fileType: ft } = item;
    updateItem(id, { status: 'uploading', progress: 0 });

    try {
      const uploadType = ft === 'paylocity+wex' ? 'combined' : ft;
      let gcs_path = null;
      let ingest;
      try {
        gcs_path = await uploadToGcs(id, file, uploadType);
        updateItem(id, { status: 'bucket_loaded', progress: 100, gcsPath: gcs_path, error: null });

        updateItem(id, { status: 'db_loading' });
        ingest = await ingestFromGcs(uploadType, gcs_path);
      } catch (primaryErr) {
        if (!isNetworkLikeError(primaryErr)) {
          throw primaryErr;
        }
        // Fallback path for local/dev where browser->GCS signed-url upload may be blocked.
        updateItem(id, {
          status: 'db_loading',
          error: null,
        });
        ingest = await directUploadToBackend(id, file, uploadType);
        gcs_path = ingest?.gcs_path || gcs_path;
        updateItem(id, { status: 'bucket_loaded', progress: 100, gcsPath: gcs_path });
      }

      if (ingest.status === 'skipped_duplicate') {
        updateItem(id, {
          status: 'skipped',
          progress: 100,
          gcsPath: gcs_path,
          loadDetails: {
            load_status: ingest.status,
            paylocity_rows: ingest.paylocity_rows,
            wex_rows: ingest.wex_rows,
            rollup_refresh: ingest.rollup_refresh,
            archive_path: ingest.archive_path,
          },
        });
        auditLog(getIdToken, 'UPLOAD', 'FILE', file.name, {
          file_type: uploadType,
          gcs_path,
          size: file.size,
          load_status: ingest.status,
          paylocity_rows: ingest.paylocity_rows,
          wex_rows: ingest.wex_rows,
        });
        return;
      }

      updateItem(id, { status: 'kpi_refreshing' });

      // KPI rollup runs in background on the server — don't fail the upload if it is
      // still in progress ('triggered') or was not yet complete ('skipped').
      // Only surface a hard error if the server explicitly returned 'failed'.
      if (ingest.rollup_refresh === 'failed') {
        console.warn('KPI rollup failed after upload — dashboard may be stale.');
      }

      updateItem(id, {
        status: 'success',
        progress: 100,
        gcsPath: gcs_path,
        loadDetails: {
          paylocity_rows: ingest.paylocity_rows,
          wex_rows: ingest.wex_rows,
          rollup_refresh: ingest.rollup_refresh,
          archive_path: ingest.archive_path,
        },
      });
      auditLog(getIdToken, 'UPLOAD', 'FILE', file.name, {
        file_type: uploadType,
        gcs_path,
        size: file.size,
        load_status: ingest.status,
        paylocity_rows: ingest.paylocity_rows,
        wex_rows: ingest.wex_rows,
      });
    } catch (err) {
      updateItem(id, {
        status: 'error',
        error: friendlyError(err, 'Upload failed.'),
      });
    }
  }, [uploadToGcs, ingestFromGcs, getIdToken, updateItem]);

  /* ── Render ── */
  const pendingCount = queue.filter(i => i.status === 'idle' || i.status === 'uploading').length;

  return (
    <div className={uploaderOnly ? undefined : 'sol-shell'} style={uploaderOnly ? { minHeight: '100vh', background: '#f3f6fb', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" } : undefined}>

      {/* ── Full sidebar for admin/verified users ── */}
      {!uploaderOnly && (
        <>
          {mobileMenuOpen && (
            <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
          )}
          <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <div className="logo">
              <h1><div className="logo-icon"><AppIcon name="truck" /></div><span>SOL Transport</span></h1>
            </div>
            <div className="nav-section">
              <div className="nav-section-title">Main Menu</div>
              <Link className="nav-item" to="/kpi-dashboard"><span className="nav-item-icon"><AppIcon name="dashboard" /></span>Dashboard</Link>
            </div>
            <div className="nav-section">
              <div className="nav-section-title">Administration</div>
              {isAdmin && <Link className="nav-item" to="/user-management"><span className="nav-item-icon"><AppIcon name="users" /></span>User Management</Link>}
              {isAdmin && <Link className="nav-item" to="/source-config"><span className="nav-item-icon"><AppIcon name="link" /></span>Source Config</Link>}
              {isAdmin && <Link className="nav-item" to="/activity-log"><span className="nav-item-icon"><AppIcon name="activity" /></span>User Activity Log</Link>}
              {canUpload && <Link className="nav-item active" to="/upload"><span className="nav-item-icon"><AppIcon name="upload" /></span>Upload Files</Link>}
            </div>
            <div className="sidebar-user">
              <div className="user-avatar">{avatar}</div>
              <div className="user-info">
                <div className="user-name">{displayName}</div>
                <div className="user-role">{isAdmin ? 'Admin' : 'Verified'}</div>
              </div>
              <button className="logout-btn" type="button" onClick={handleLogout} aria-label="Logout">
                <AppIcon name="login" size={14} />Logout
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ── Simple top bar for pure uploaders ── */}
      {uploaderOnly && (
        <div style={{
          background: '#ffffff', borderBottom: '1px solid #e8ecf1',
          padding: '0 24px', height: 56, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <AppIcon name="truck" size={20} /> SOL Transport
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>{displayName}</span>
            <button onClick={handleLogout} style={{
              background: 'none', border: '1px solid #e5e7eb', color: '#374151',
              borderRadius: 6, padding: '5px 12px', fontSize: 13, cursor: 'pointer',
            }}>Logout</button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className={uploaderOnly ? undefined : 'main-content'} style={uploaderOnly ? { padding: '32px 24px', maxWidth: 780, margin: '0 auto' } : undefined}>
        {/* Mobile Menu Button */}
        {!uploaderOnly && (
          <button
            className="mobile-menu-btn"
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <AppIcon name="menu" size={18} />
          </button>
        )}

        <header className="header">
          <div className="header-left">
            <h2 className="title-with-icon"><AppIcon name="upload" /> File Upload</h2>
            <p>Upload WEX or Paylocity Excel files — stored securely and loaded immediately</p>
          </div>
        </header>

        <div style={{ maxWidth: 720 }}>
        {/* File type selector */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { value: 'paylocity', label: 'Paylocity' },
            { value: 'wex',       label: 'WEX' },
            { value: 'paylocity+wex', label: 'Paylocity + WEX' },
          ].map(({ value, label }) => (
            <button key={value} onClick={() => setFileType(value)}
              className={`header-btn${fileType === value ? ' primary' : ''}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Drop zone */}
        <div className="chart-card"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#3b82f6' : '#e5e7eb'}`,
            padding: '40px 24px', textAlign: 'center',
            cursor: 'pointer', background: dragging ? 'rgba(59,130,246,0.04)' : 'rgba(255,255,255,0.9)',
            transition: 'all 0.15s ease', marginBottom: 24,
          }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
          <div style={{ color: '#475569', fontSize: 15, fontWeight: 500 }}>
            Drag & drop{' '}
            {fileType === 'paylocity+wex' ? 'Paylocity + WEX' : fileType.toUpperCase()}
            {' '}Excel files here
          </div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>
            or click to browse — .xlsx and .xls files accepted
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              addFiles(Array.from(e.target.files || []));
              e.target.value = '';
            }}
          />
        </div>

        {/* Upload queue */}
        {queue.length > 0 && (
          <div className="chart-card" style={{ marginBottom: 24, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div className="chart-title">
                Upload Queue {pendingCount > 0 && <span style={{ color: '#3b82f6', fontWeight: 500, fontSize: 13 }}>({pendingCount} in progress)</span>}
              </div>
              <button onClick={() => setQueue(q => q.filter(i => i.status === 'uploading'))}
                className="header-btn" style={{ padding: '6px 12px', fontSize: 12 }}>
                Clear completed
              </button>
            </div>
            {queue.map(item => (
              <UploadRow key={item.id} item={item} onRemove={removeItem} />
            ))}
          </div>
        )}

        {/* Recent uploads list */}
        <RecentUploads apiBaseUrl={apiBaseUrl} fetchWithAuthRetry={fetchWithAuthRetry} fileType="all" />

        </div>
      </main>
    </div>
  );
}
